// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/utils/memberHistoryFormat.js
//
// Transforme un événement brut de GET /members/:id/history ({type, date,
// detail}, cf backend routes/member.js) en {iconKey, title, detail} prêt à
// afficher. Fonction PURE (pas de JSX/CIcon ici — testable sous vitest sans
// DOM, même convention que memberType.js/schoolRoles.js/careerStatus.js) :
// le composant résout iconKey → CIcon au rendu.
//
// Les valeurs scalaires (member_type, career_status, sector, ...) arrivent
// du backend en TEXTE (colonnes old_value/new_value TEXT, y compris pour les
// booléens : 'true'/'false' littéraux) — jamais de coercition de type ici,
// on compare des chaînes.

import { getMemberTypeShortLabel } from './memberType'
import { getCareerStatusLabel } from './careerStatus'
import { getSectorLabel } from './sectors'
import { getSchoolRoleLabel } from './schoolRoles'

const transition = (t, from, to) =>
  t('memberHistory:transition', {
    from: from || t('memberHistory:not_set'),
    to: to || t('memberHistory:not_set'),
  })

export function formatHistoryEvent(event, t) {
  const { type, detail = {} } = event || {}

  switch (type) {
    case 'member_type':
      return {
        iconKey: 'flag',
        title: t('memberHistory:event_title.member_type'),
        detail: transition(
          t,
          getMemberTypeShortLabel(detail.oldValue, t),
          getMemberTypeShortLabel(detail.newValue, t),
        ),
      }

    case 'graduation_status':
      return {
        iconKey: 'flag',
        title: t('memberHistory:event_title.graduation_status'),
        detail: transition(
          t,
          detail.oldValue ? t(`memberHistory:graduation_status.${detail.oldValue}`) : '',
          detail.newValue ? t(`memberHistory:graduation_status.${detail.newValue}`) : '',
        ),
      }

    case 'is_ambassador':
      return {
        iconKey: 'star',
        title:
          detail.newValue === 'true'
            ? t('memberHistory:event_title.is_ambassador_true')
            : t('memberHistory:event_title.is_ambassador_false'),
        detail: null,
      }

    case 'school_roles': {
      const added = (detail.added || []).map((r) => getSchoolRoleLabel(r, t))
      const removed = (detail.removed || []).map((r) => getSchoolRoleLabel(r, t))
      const parts = []
      if (added.length)
        parts.push(t('memberHistory:school_roles_added', { roles: added.join(', ') }))
      if (removed.length)
        parts.push(t('memberHistory:school_roles_removed', { roles: removed.join(', ') }))
      return {
        iconKey: 'briefcase',
        title: t('memberHistory:event_title.school_roles'),
        detail: parts.join(' — ') || null,
      }
    }

    case 'career_status':
      return {
        iconKey: 'briefcase',
        title: t('memberHistory:event_title.career_status'),
        detail: transition(
          t,
          getCareerStatusLabel(detail.oldValue, t),
          getCareerStatusLabel(detail.newValue, t),
        ),
      }

    case 'sector':
      return {
        iconKey: 'briefcase',
        title: t('memberHistory:event_title.sector'),
        detail: transition(
          t,
          getSectorLabel(detail.oldValue, t),
          getSectorLabel(detail.newValue, t),
        ),
      }

    case 'suspended':
      return {
        iconKey: 'lock',
        title:
          detail.newValue === 'true'
            ? t('memberHistory:event_title.suspended_true')
            : t('memberHistory:event_title.suspended_false'),
        detail: null,
      }

    case 'donation':
      return {
        iconKey: 'cash',
        title: t('memberHistory:event_title.donation'),
        detail: t('memberHistory:amount', { amount: detail.amount, currency: detail.currency }),
      }

    case 'contribution':
      return {
        iconKey: 'cash',
        title: t('memberHistory:event_title.contribution'),
        detail: t('memberHistory:amount', { amount: detail.amount, currency: detail.currency }),
      }

    case 'event_registration':
      return {
        iconKey: 'calendar',
        title: t('memberHistory:event_title.event_registration'),
        detail: detail.eventTitle || null,
      }

    case 'admin_edit':
      return {
        iconKey: 'pencil',
        title: t('memberHistory:event_title.admin_edit'),
        detail: (detail.changedFields || []).join(', ') || null,
      }

    default:
      return { iconKey: 'flag', title: type, detail: null }
  }
}
